import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaArrowLeft,
  FaCheckCircle,
  FaClipboardCheck,
  FaFileInvoice,
  FaSpinner,
  FaTools,
  FaWhatsapp,
  FaLink,
} from 'react-icons/fa';
import { jobCardService } from '../../../../services/apiService';
import { normalizeAmendmentUrl } from '../../../../services/bridgeAmendmentApi';
import { normalizeCollectionUrl } from '../../../../services/bridgeCollectionApi';
import InfoAlertCard from '../../../common/InfoAlertCard';

/** Post-acceptance pipeline (shown after garage confirms allocation) */
const POST_ACCEPT_STAGES = [
  'Mechanic assigned',
  'Intake scheduled',
  'Preliminary notes',
  'Status: INTAKE',
  'Diagnosis & quote'
];

const SERVICE_MODES = [
  { value: 'customer_visit', label: 'Customer visits garage' },
  { value: 'garage_pickup', label: 'Garage pickup' },
  { value: 'onsite_repair', label: 'On-site repair' }
];

const REJECT_PRESETS = [
  { value: 'no_mechanic', label: 'No available mechanic', text: 'No available mechanic with required skills.' },
  { value: 'full_capacity', label: 'Garage at full capacity', text: 'Garage at full capacity.' },
  { value: 'not_supported', label: 'Service not supported', text: 'Service not supported at this garage.' },
  { value: 'outside_area', label: 'Outside service area', text: 'Pickup/repair location outside our service area.' }
];

const getWorkflowIndex = (status) => {
  switch (status) {
    case 'Created':
      return 2;
    case 'Intake':
      return 2;
    case 'PendingCustomerApproval':
      return 5;
    case 'CustomerApproved':
    case 'PartsOrdered':
      return 6;
    case 'InProgress':
      return 6;
    case 'QCRejected':
      return 6;
    case 'ReadyForQC':
    case 'QCApproved':
      return 7;
    case 'Billed':
    case 'Paid':
      return 7;
    case 'Delivered':
    case 'Closed':
      return 8;
    default:
      return status ? 1 : 0;
  }
};

const LATE_WORKFLOW = [
  'INTAKE',
  'DIAGNOSIS',
  'QUOTATION',
  'APPROVAL',
  'IN_PROGRESS',
  'QUALITY_CHECK',
  'DELIVERED'
];

function customerName(c) {
  if (!c) return '—';
  const n = (c.name || c.fullName || '').trim();
  if (n) return n;
  const f = [c.firstName, c.lastName].filter(Boolean).join(' ').trim();
  return f || c.phone || '—';
}

function formatAddr(addr) {
  if (!addr || typeof addr !== 'object') return '—';
  return [addr.fullAddress, addr.area, addr.city, addr.state, addr.postalCode].filter(Boolean).join(', ') || '—';
}

/** Use current browser origin so Preview/copy work even if backend FRONTEND_URL is stale */
function normalizeBridgeUrl(url) {
  if (!url) return '';
  try {
    const parsed = new URL(url);
    const token = decodeURIComponent(parsed.pathname.replace(/^\/bridge\//, ''));
    if (!token) return url;
    return `${window.location.origin}/bridge/${token}`;
  } catch {
    const match = String(url).match(/\/bridge\/([^?#]+)/);
    if (match) {
      return `${window.location.origin}/bridge/${match[1]}`;
    }
    return url;
  }
}

const JobCardWorkflow = ({ jobId }) => {
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(false);
  const [action, setAction] = useState('');
  const [bridgeLink, setBridgeLink] = useState('');
  const [bridgeLoading, setBridgeLoading] = useState(false);
  const [collectionLink, setCollectionLink] = useState('');
  const [collectionLoading, setCollectionLoading] = useState(false);
  const [bridgePartsAlert, setBridgePartsAlert] = useState(false);
  const [amendmentLink, setAmendmentLink] = useState('');
  const [amendmentLoading, setAmendmentLoading] = useState(false);
  const [amendmentForm, setAmendmentForm] = useState({
    plainIssue: '',
    partName: '',
    partPrice: '',
    laborAmount: '',
  });

  const [estimate, setEstimate] = useState({
    laborItems: [{ description: 'General Labor', hours: 1, rate: 500 }],
    parts: [],
    estimatedCompletionDate: ''
  });

  const [intakeForm, setIntakeForm] = useState({
    assignedMechanicId: '',
    scheduledTime: '',
    serviceMode: '',
    preliminaryNotes: '',
    customerDetailsVerified: false,
    damageImagesReviewed: false,
    resourcesPrepared: false
  });

  const refresh = async () => {
    setLoading(true);
    try {
      const jc = await jobCardService.getById(jobId);
      setJob(jc);
      const gi = jc?.garageIntake || {};
      setIntakeForm({
        assignedMechanicId: gi.assignedMechanicId || '',
        scheduledTime: gi.scheduledTime ? new Date(gi.scheduledTime).toISOString().slice(0, 16) : '',
        serviceMode: gi.serviceMode || '',
        preliminaryNotes: gi.preliminaryNotes || '',
        customerDetailsVerified: !!gi.customerDetailsVerified,
        damageImagesReviewed: !!gi.damageImagesReviewed,
        resourcesPrepared: !!gi.resourcesPrepared
      });
      setEstimate((prev) => ({
        laborItems: jc?.estimate?.laborItems?.length ? jc.estimate.laborItems : prev.laborItems,
        parts: jc?.estimate?.parts?.length ? jc.estimate.parts : prev.parts,
        estimatedCompletionDate: jc?.estimate?.estimatedCompletionDate
          ? new Date(jc.estimate.estimatedCompletionDate).toISOString().slice(0, 16)
          : prev.estimatedCompletionDate
      }));
    } catch (e) {
      setJob(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  useEffect(() => {
    setStepperFocus(null);
  }, [jobId, job?.status]);

  const saveIntake = async (partial = {}) => {
    const next = { ...intakeForm, ...partial };
    setIntakeForm(next);
    try {
      setAction('saveIntake');
      await jobCardService.updateGarageIntake(jobId, {
        assignedMechanicId: next.assignedMechanicId,
        scheduledTime: next.scheduledTime || null,
        serviceMode: next.serviceMode,
        preliminaryNotes: next.preliminaryNotes,
        customerDetailsVerified: next.customerDetailsVerified,
        damageImagesReviewed: next.damageImagesReviewed,
        resourcesPrepared: next.resourcesPrepared
      });
      await refresh();
    } catch (e) {
      alert(e.message || 'Failed to save');
    } finally {
      setAction('');
    }
  };

  const totals = useMemo(() => {
    const labor = (estimate.laborItems || []).reduce((sum, li) => sum + (Number(li.hours) || 0) * (Number(li.rate) || 0), 0);
    const parts = (estimate.parts || []).reduce((sum, p) => sum + (Number(p.price ?? p.unitPrice) || 0) * (Number(p.quantity) || 0), 0);
    const subtotal = labor + parts;
    const tax = subtotal * 0.18;
    const total = subtotal + tax;
    return {
      labor: Number(labor.toFixed(2)),
      parts: Number(parts.toFixed(2)),
      subtotal: Number(subtotal.toFixed(2)),
      tax: Number(tax.toFixed(2)),
      total: Number(total.toFixed(2))
    };
  }, [estimate.laborItems, estimate.parts]);

  const workflowIndex = useMemo(() => getWorkflowIndex(job?.status), [job?.status]);
  const currentStageLabel = useMemo(() => {
    switch (job?.status) {
      case 'Billed':
        return 'INVOICE / COLLECTION';
      case 'Paid':
        return 'COLLECTION';
      case 'Delivered':
      case 'Closed':
        return 'DELIVERED';
      default:
        return LATE_WORKFLOW[Math.min(workflowIndex - 2, LATE_WORKFLOW.length - 1)] || 'UNKNOWN';
    }
  }, [job?.status, workflowIndex]);

  const canEditEstimateAfterQc =
    job?.status === 'QCRejected' || (job?.status === 'InProgress' && !!job?.qc?.garageMayReviseEstimate);

  const alloc = job?.allocation?.status || 'pending';
  const isPendingAlloc = alloc === 'pending';
  const isReallocated = alloc === 'reallocate';
  const isAccepted = alloc === 'confirmed';
  const gi = job?.garageIntake || {};
  const hasPhotos = Array.isArray(job?.customerPhotos) && job.customerPhotos.length > 0;
  const canConfirmIntake =
    isAccepted &&
    job?.status === 'Created' &&
    String(intakeForm.assignedMechanicId || '').trim() &&
    intakeForm.scheduledTime &&
    intakeForm.serviceMode;

  const intakeProgress = useMemo(
    () => ({
      mechanic: !!String(gi.assignedMechanicId || '').trim(),
      schedule: !!(gi.scheduledTime && gi.serviceMode),
      notes: !!String(gi.preliminaryNotes || '').trim()
    }),
    [gi]
  );

  /** How many post-accept milestones are done (for progress chips) */
  const postAcceptCompleted = useMemo(() => {
    if (!isAccepted) return 0;
    let n = 0;
    if (intakeProgress.mechanic) n++;
    if (intakeProgress.schedule) n++;
    if (intakeProgress.notes) n = Math.max(n, 3);
    if (job?.status === 'Intake') n = Math.max(n, 4);
    if (job?.status === 'PendingCustomerApproval') n = 5;
    return Math.min(n, POST_ACCEPT_STAGES.length);
  }, [isAccepted, intakeProgress, job?.status]);

  const addPart = () => {
    setEstimate((p) => ({
      ...p,
      parts: [
        ...(p.parts || []),
        {
          sku: `SKU-${Math.floor(Math.random() * 10000)}`,
          name: '',
          quantity: 1,
          price: 0,
          unitPrice: 0,
          mrp: 0,
          tax: 18,
          source: 'In Stock',
          leadTime: 'Immediate',
          mandatory: false
        }
      ]
    }));
  };

  const addLabor = () => {
    setEstimate((p) => ({
      ...p,
      laborItems: [...(p.laborItems || []), { description: '', hours: 1, rate: 500 }]
    }));
  };

  const hydrateEstimateFromJob = () => {
    if (!job) return;
    setEstimate((prev) => ({
      laborItems:
        job.estimate?.laborItems?.length > 0
          ? job.estimate.laborItems.map((li) => ({ ...li }))
          : prev.laborItems?.length
            ? prev.laborItems
            : [{ description: 'General Labor', hours: 1, rate: 500 }],
      parts:
        Array.isArray(job.estimate?.parts) && job.estimate.parts.length > 0
          ? job.estimate.parts.map((p) => ({ ...p }))
          : prev.parts || [],
      estimatedCompletionDate: job.estimate?.estimatedCompletionDate
        ? new Date(job.estimate.estimatedCompletionDate).toISOString().slice(0, 16)
        : prev.estimatedCompletionDate
    }));
  };

  const toggleQuotationStep = () => {
    if (stepperFocus === 'QUOTATION') {
      setStepperFocus(null);
    } else {
      hydrateEstimateFromJob();
      setStepperFocus('QUOTATION');
    }
  };

  const updatePart = (idx, field, value) => {
    setEstimate((p) => {
      const parts = [...(p.parts || [])];
      const next = { ...parts[idx], [field]: value };
      // Keep pricing fields in sync: backend prefers `price` for totals.
      if (field === 'price') next.unitPrice = value;
      if (field === 'unitPrice') next.price = value;
      parts[idx] = next;
      return { ...p, parts };
    });
  };

  const updateLabor = (idx, field, value) => {
    setEstimate((p) => {
      const laborItems = [...(p.laborItems || [])];
      laborItems[idx] = { ...laborItems[idx], [field]: value };
      return { ...p, laborItems };
    });
  };

  const submitEstimate = async () => {
    try {
      setAction('estimate');
      const isRevision = job?.status === 'PendingCustomerApproval';
      const postQcRevision = canEditEstimateAfterQc;
      await jobCardService.submitEstimate(jobId, {
        estimate: {
          laborItems: estimate.laborItems,
          parts: estimate.parts,
          estimatedCompletionDate: estimate.estimatedCompletionDate || null
        },
        note: isRevision ? 'Quotation updated' : postQcRevision ? 'Estimate updated after QC rejection' : 'Estimate submitted'
      });
      await refresh();
    } catch (e) {
      alert(e.message || 'Failed to submit estimate');
    } finally {
      setAction('');
    }
  };

  const goStatus = async (status, note) => {
    try {
      setAction(status);
      await jobCardService.updateStatus(jobId, status, { note });
      await refresh();
    } catch (e) {
      alert(e.message || 'Failed to update status');
    } finally {
      setAction('');
    }
  };

  const [deliverModal, setDeliverModal] = useState(false);
  const [deliverForm, setDeliverForm] = useState({ receiverName: '', photo: '', signature: '' });
  const [rejectModal, setRejectModal] = useState({ open: false, preset: '', reason: '' });
  /** Late stepper: which stage panel is open (e.g. QUOTATION editor) */
  const [stepperFocus, setStepperFocus] = useState(null);

  const deliver = async () => {
    if (!deliverForm.receiverName?.trim()) {
      alert('Receiver name is required');
      return;
    }
    try {
      setAction('deliver');
      await jobCardService.deliver(jobId, {
        receiverName: deliverForm.receiverName,
        photo: deliverForm.photo || undefined,
        signature: deliverForm.signature || undefined
      });
      setDeliverModal(false);
      setDeliverForm({ receiverName: '', photo: '', signature: '' });
      await refresh();
    } catch (e) {
      alert(e.message || 'Failed to deliver');
    } finally {
      setAction('');
    }
  };

  const acceptJob = async () => {
    try {
      setAction('accept');
      await jobCardService.accept(jobId);
      await refresh();
    } catch (e) {
      alert(e.message || 'Failed to accept');
    } finally {
      setAction('');
    }
  };

  const confirmIntakeStatus = async () => {
    try {
      setAction('confirmIntake');
      await jobCardService.updateGarageIntake(jobId, {
        assignedMechanicId: intakeForm.assignedMechanicId,
        scheduledTime: intakeForm.scheduledTime || null,
        serviceMode: intakeForm.serviceMode,
        preliminaryNotes: intakeForm.preliminaryNotes,
        // UI no longer asks for these review checkboxes, but backend expects customerDetailsVerified
        customerDetailsVerified: true,
        damageImagesReviewed: true
      });
      await jobCardService.confirmIntake(jobId);
      await refresh();
    } catch (e) {
      alert(e.message || 'Could not set status to INTAKE. Check mechanic, schedule, and service mode.');
    } finally {
      setAction('');
    }
  };

  const handleGenerateBridgeLink = async () => {
    const estimateParts = job?.estimate?.parts || [];
    if (!estimateParts.length) {
      setBridgePartsAlert(true);
      return;
    }

    setBridgeLoading(true);
    try {
      const data = await jobCardService.generateBridgeLink(jobId, {
        diagnosisSummary:
          job?.complaintDescription ||
          (Array.isArray(job?.reportedIssues) ? job.reportedIssues.join('; ') : '') ||
          'Your garage has parts that need your approval.',
      });
      const rawUrl = data?.bridgeUrl || '';
      const url = normalizeBridgeUrl(rawUrl);
      setBridgeLink(url);
      if (url && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      }
    } catch (e) {
      alert(e.message || 'Could not generate customer approval link');
    } finally {
      setBridgeLoading(false);
    }
  };

  const customerWhatsAppBridgeUrl = useMemo(() => {
    const link = normalizeBridgeUrl(bridgeLink);
    if (!link) return '';
    const phone = (job?.customerId?.phone || '').replace(/\D/g, '');
    const msg = encodeURIComponent(
      `Hi, please review and approve the parts for your vehicle (${job?.vehicle?.registrationNumber || 'your car'}): ${link}`
    );
    return phone ? `https://wa.me/${phone}?text=${msg}` : `https://wa.me/?text=${msg}`;
  }, [bridgeLink, job]);

  const displayBridgeLink = normalizeBridgeUrl(bridgeLink);
  const displayAmendmentLink = normalizeAmendmentUrl(amendmentLink);
  const displayCollectionLink = normalizeCollectionUrl(collectionLink);

  const handleGenerateCollectionLink = async () => {
    setCollectionLoading(true);
    try {
      const data = await jobCardService.generateCollectionLink(jobId);
      const rawUrl = data?.collectionUrl || '';
      const url = normalizeCollectionUrl(rawUrl);
      setCollectionLink(url);
      if (url && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      }
    } catch (e) {
      alert(e.message || 'Could not generate collection link');
    } finally {
      setCollectionLoading(false);
    }
  };

  const customerWhatsAppCollectionUrl = useMemo(() => {
    const link = normalizeCollectionUrl(collectionLink);
    if (!link) return '';
    const phone = (job?.customerId?.phone || '').replace(/\D/g, '');
    const msg = encodeURIComponent(
      `Hi, your vehicle (${job?.vehicle?.registrationNumber || 'your car'}) is ready for pickup. Pay labour & collect here: ${link}`
    );
    return phone ? `https://wa.me/${phone}?text=${msg}` : `https://wa.me/?text=${msg}`;
  }, [collectionLink, job]);

  const handleGenerateAmendmentLink = async () => {
    const plainIssue = amendmentForm.plainIssue.trim();
    if (plainIssue.length < 3) {
      alert('Describe the new issue in plain language (at least 3 characters).');
      return;
    }
    const partPrice = Number(amendmentForm.partPrice) || 0;
    const laborAmount = Number(amendmentForm.laborAmount) || 0;
    const partName = amendmentForm.partName.trim();
    if (!partName && partPrice <= 0 && laborAmount <= 0) {
      alert('Add at least one additional part or a labour charge.');
      return;
    }

    setAmendmentLoading(true);
    try {
      const payload = {
        plainIssue,
        additionalParts: partName
          ? [
              {
                plainName: partName,
                price: partPrice || 0,
                conditionLabel: 'As recommended by mechanic',
                warrantyDays: 30,
                quantity: 1,
              },
            ]
          : [],
        additionalLabor: laborAmount
          ? { description: 'Additional repair labour', amount: laborAmount }
          : {},
      };
      const data = await jobCardService.generateAmendmentLink(jobId, payload);
      const url = normalizeAmendmentUrl(data?.amendmentUrl || '');
      setAmendmentLink(url);
      if (url && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      }
    } catch (e) {
      alert(e.message || 'Could not generate amendment link');
    } finally {
      setAmendmentLoading(false);
    }
  };

  if (loading && !job) return <div className="p-8">Loading…</div>;
  if (!job) return <div className="p-8">Job not found.</div>;

  const cust = job.customerId;
  const showIntakeWizard = isAccepted && ['Created', 'Intake'].includes(job.status);
  const showEstimate = ['Created', 'Intake'].includes(job.status) && isAccepted;
  const estimateLockedToIntake = job.status === 'Created' && isAccepted;

  const liveQuotationSummary = stepperFocus === 'QUOTATION' && job.status === 'PendingCustomerApproval';
  const estimateSummarySubtotal = liveQuotationSummary ? totals.subtotal : Number(job.estimate?.subtotal || 0);
  const estimateSummaryTax = liveQuotationSummary ? totals.tax : Number(job.estimate?.tax || 0);
  const estimateSummaryTotal = liveQuotationSummary ? totals.total : Number(job.estimate?.total || 0);

  return (
    <div className="max-w-6xl mx-auto space-y-6 pb-10">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <button type="button" onClick={() => navigate('/garage/dashboard/jobs')} className="text-gray-600 hover:text-gray-900 flex items-center gap-2">
          <FaArrowLeft /> Back to Jobs
        </button>
        <div className="text-right text-sm text-gray-700 space-y-1">
          <div>
            <span className="font-semibold">Repair stage:</span>{' '}
            <span className="font-black">{currentStageLabel}</span>
          </div>
          <div className="text-xs text-gray-500">
            Job status: <span className="font-mono">{job.status}</span>
            {job.allocation?.status && (
              <>
                {' '}
                · Allocation: <span className="font-mono">{job.allocation.status}</span>
              </>
            )}
          </div>
        </div>
      </div>

      {job.status === 'QCRejected' && (
        <div className="rounded-xl border-2 border-red-200 bg-red-50/90 p-4 text-sm text-red-950 space-y-2">
          <p className="font-bold text-red-900">QC rejected — address this before resubmitting</p>
          <p className="whitespace-pre-wrap text-red-900/90">{job.qc?.lastRejectionReason || 'No reason provided.'}</p>
          <button
            type="button"
            onClick={() => {
              hydrateEstimateFromJob();
              setStepperFocus('QUOTATION');
            }}
            className="px-4 py-2 rounded-lg bg-white border border-red-300 text-red-900 font-semibold text-xs hover:bg-red-100"
          >
            Edit labor, parts &amp; dates
          </button>
        </div>
      )}

      {job.status === 'InProgress' && job.qc?.garageMayReviseEstimate && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950">
          <p className="font-semibold">QC requested changes — you can still update the estimate until you mark ready for QC.</p>
          <button
            type="button"
            onClick={() => {
              hydrateEstimateFromJob();
              setStepperFocus('QUOTATION');
            }}
            className="mt-2 px-4 py-2 rounded-lg bg-white border border-amber-300 text-amber-900 font-semibold text-xs hover:bg-amber-100"
          >
            Open labor &amp; parts editor
          </button>
        </div>
      )}

      {/* ——— 1. Accept / Reject ——— */}
      {isPendingAlloc && (
        <div className="rounded-xl border-2 border-amber-200 bg-amber-50/80 p-5 shadow-sm space-y-4">
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-amber-500 text-white text-sm font-black">
              1
            </span>
            Review and decide (accept or reallocate)
          </h2>
          <p className="text-sm text-gray-700 mt-2 mb-4">
            First, review the <strong>customer details</strong>, <strong>vehicle</strong>, and any <strong>damage photos</strong>.
            Then choose whether to accept. If you can’t take this job, reallocate it and mention why so the customer can see the
            reason.
          </p>

          {/* Review block (before decision) */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white/80 rounded-xl border border-amber-200 p-4">
              <h3 className="text-sm font-black text-gray-900 mb-2">Vehicle & complaint</h3>
              <div className="text-sm text-gray-700 space-y-1">
                <div>
                  <span className="font-semibold">Vehicle:</span>{' '}
                  {[job.vehicle?.make, job.vehicle?.model, job.vehicle?.variant].filter(Boolean).join(' ') || '—'}
                </div>
                <div>
                  <span className="font-semibold">Registration:</span> {job.vehicle?.registrationNumber || '—'}
                </div>
                <div>
                  <span className="font-semibold">Complaint:</span> {job.complaintDescription || '—'}
                </div>
                <div>
                  <span className="font-semibold">Preferred time:</span>{' '}
                  {job.preferredServiceDate ? new Date(job.preferredServiceDate).toLocaleString() : '—'}
                </div>
              </div>
            </div>
            <div className="bg-white/80 rounded-xl border border-amber-200 p-4">
              <h3 className="text-sm font-black text-gray-900 mb-2">Customer details</h3>
              <div className="text-sm text-gray-700 space-y-1">
                <div>
                  <span className="font-semibold">Name:</span> {customerName(cust)}
                </div>
                <div>
                  <span className="font-semibold">Phone:</span>{' '}
                  {cust?.phone ? `${cust.phoneCode || '+91'} ${cust.phone}` : '—'}
                </div>
                <div>
                  <span className="font-semibold">Address:</span> {formatAddr(cust?.address)}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white/80 rounded-xl border border-amber-200 p-4">
            <h3 className="text-sm font-black text-gray-900 mb-2">Damage photos (if any)</h3>
            {hasPhotos ? (
              <div className="flex flex-wrap gap-2">
                {job.customerPhotos.map((url, idx) => (
                  <a
                    key={url + idx}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-24 h-24 rounded-lg overflow-hidden border border-gray-200 bg-gray-50 hover:ring-2 hover:ring-indigo-500"
                  >
                    <img src={url} alt="" className="w-full h-full object-cover" />
                  </a>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 italic">No photos uploaded.</p>
            )}
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={acceptJob}
              disabled={action === 'accept'}
              className="px-5 py-2.5 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 disabled:opacity-50"
            >
              {action === 'accept' ? 'Accepting…' : 'Accept job'}
            </button>
            <button
              type="button"
              onClick={() => setRejectModal({ open: true, preset: '', reason: '' })}
              disabled={!!action}
              className="px-5 py-2.5 border-2 border-amber-700 text-amber-900 font-semibold rounded-lg hover:bg-amber-100"
            >
              Reallocate (can&apos;t take this)
            </button>
          </div>
        </div>
      )}

      {isReallocated && (
        <div className="rounded-xl border border-gray-300 bg-gray-100 p-4 text-sm text-gray-700">
          This job was <strong>rejected</strong> or <strong>reallocated</strong>.
          {job.allocation?.reason && (
            <span className="block mt-1 text-gray-600">Reason on file: {job.allocation.reason}</span>
          )}
        </div>
      )}

      {/* Post-accept checklist strip */}
      {isAccepted && job.status === 'Created' && (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">After acceptance — complete intake</p>
          <div className="flex flex-wrap gap-1.5 text-[11px] sm:text-xs font-semibold">
            {POST_ACCEPT_STAGES.map((label, idx) => (
              <React.Fragment key={label}>
                <span
                  className={`px-2 py-1 rounded-full border ${
                    idx < postAcceptCompleted ? 'bg-emerald-50 border-emerald-400 text-emerald-800' : 'border-gray-200 text-gray-500'
                  }`}
                >
                  {idx + 2}. {label}
                </span>
                {idx < POST_ACCEPT_STAGES.length - 1 && <span className="text-gray-300">→</span>}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      {isAccepted && job.status === 'Intake' && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-4 text-sm">
          <strong className="text-emerald-900">Job card is in INTAKE.</strong> Complete resource prep, then send diagnosis &
          quotation.
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Job Card {job.jobCardId || job._id}</h2>
            <p className="text-sm text-gray-600">
              {job.vehicle?.registrationNumber ? `${job.vehicle.registrationNumber} • ` : ''}
              {job.vehicle?.make} {job.vehicle?.model} {job.vehicle?.variant}
            </p>
            <p className="text-sm text-gray-600 mt-1">Service / complaint: {job.complaintDescription}</p>
          </div>
        </div>
      </div>

      {/* ——— Steps 2–8 (after accept) ——— */}
      {showIntakeWizard && (
        <div className="space-y-4">
          <section className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="text-base font-bold text-gray-900">Intake setup (single form)</h3>
            <p className="text-sm text-gray-600 mt-1">
              Assign mechanic, schedule the intake, and add preliminary diagnosis notes. Then set status to <span className="font-mono">INTAKE</span>.
            </p>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">assignedMechanicId</label>
                <input
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  value={intakeForm.assignedMechanicId}
                  onChange={(e) => setIntakeForm((p) => ({ ...p, assignedMechanicId: e.target.value }))}
                  placeholder="MEC-8274"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">scheduled_time</label>
                <input
                  type="datetime-local"
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  value={intakeForm.scheduledTime}
                  onChange={(e) => setIntakeForm((p) => ({ ...p, scheduledTime: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">service_mode</label>
                <select
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  value={intakeForm.serviceMode}
                  onChange={(e) => setIntakeForm((p) => ({ ...p, serviceMode: e.target.value }))}
                >
                  <option value="">Select…</option>
                  {SERVICE_MODES.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-semibold text-gray-600 mb-1">Preliminary diagnosis (optional)</label>
                <textarea
                  rows={3}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  value={intakeForm.preliminaryNotes}
                  onChange={(e) => setIntakeForm((p) => ({ ...p, preliminaryNotes: e.target.value }))}
                  placeholder='e.g. "Possible brake pad replacement required"'
                />
              </div>
            </div>

            {!canConfirmIntake && job.status === 'Created' && (
              <ul className="mt-3 text-xs text-amber-800 list-disc list-inside">
                {!String(intakeForm.assignedMechanicId || '').trim() && <li>Assign a mechanic</li>}
                {!intakeForm.scheduledTime && <li>Set scheduled time</li>}
                {!intakeForm.serviceMode && <li>Select service mode</li>}
              </ul>
            )}

            <div className="mt-4 flex flex-wrap gap-2">
              {job.status === 'Created' ? (
                <button
                  type="button"
                  onClick={confirmIntakeStatus}
                  disabled={!canConfirmIntake || action === 'confirmIntake'}
                  className="px-5 py-2.5 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                >
                  {action === 'confirmIntake' ? 'Updating…' : 'Save & set INTAKE'}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() =>
                    saveIntake({
                      assignedMechanicId: intakeForm.assignedMechanicId,
                      scheduledTime: intakeForm.scheduledTime,
                      serviceMode: intakeForm.serviceMode,
                      preliminaryNotes: intakeForm.preliminaryNotes
                    })
                  }
                  disabled={action === 'saveIntake'}
                  className="px-5 py-2.5 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                >
                  {action === 'saveIntake' ? 'Saving…' : 'Save intake details'}
                </button>
              )}
            </div>
          </section>

        </div>
      )}

      {/* Late workflow breadcrumb */}
      {[
        'PendingCustomerApproval',
        'CustomerApproved',
        'PartsOrdered',
        'InProgress',
        'ReadyForQC',
        'QCRejected',
        'QCApproved',
        'Billed',
        'Paid',
        'Delivered',
        'Closed'
      ].includes(job.status) && (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex flex-wrap items-center gap-2 text-xs md:text-sm font-semibold text-gray-600">
            {LATE_WORKFLOW.map((label, idx) => {
              const active = workflowIndex >= idx + 2;
              const isLast = idx === LATE_WORKFLOW.length - 1;
              const isQuotation = label === 'QUOTATION';
              const quotationOpen = stepperFocus === 'QUOTATION';
              return (
                <React.Fragment key={label}>
                  {isQuotation && active ? (
                    <button
                      type="button"
                      onClick={toggleQuotationStep}
                      title={
                        job.status === 'PendingCustomerApproval'
                          ? 'Edit quotation (labor & parts)'
                          : 'View quotation details'
                      }
                      className={`px-2 py-1 rounded-full border transition ${
                        quotationOpen
                          ? 'bg-indigo-100 border-indigo-600 text-indigo-900 ring-2 ring-indigo-300'
                          : 'bg-indigo-50 border-indigo-400 text-indigo-700 hover:bg-indigo-100 cursor-pointer'
                      }`}
                    >
                      {label}
                    </button>
                  ) : (
                    <span
                      className={`px-2 py-1 rounded-full border ${
                        active ? 'bg-indigo-50 border-indigo-400 text-indigo-700' : 'border-gray-200 text-gray-500'
                      }`}
                    >
                      {label}
                    </span>
                  )}
                  {!isLast && <span className="text-gray-400">→</span>}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      )}

      {stepperFocus === 'QUOTATION' && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4 min-h-[8rem]">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h3 className="text-lg font-bold text-gray-900">Quotation</h3>
            <button
              type="button"
              onClick={() => setStepperFocus(null)}
              className="text-sm font-semibold text-gray-600 hover:text-gray-900"
            >
              Close
            </button>
          </div>

          {job.status === 'PendingCustomerApproval' || canEditEstimateAfterQc ? (
            <>
              <p className="text-sm text-gray-600">
                {job.status === 'PendingCustomerApproval' ? (
                  <>
                    Edit labor and parts. The estimate summary below updates as you type. Click save to update the quotation sent
                    for customer approval.
                  </>
                ) : (
                  <>
                    QC was rejected. Update labor, parts, and completion date as needed, save, then use <strong>Resubmit for QC</strong>{' '}
                    (or <strong>Continue repair</strong> first if more workshop work is required).
                  </>
                )}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <h4 className="text-sm font-bold text-gray-700 flex items-center justify-between">
                    Labor
                    <button type="button" onClick={addLabor} className="text-xs font-bold text-blue-600 hover:text-blue-700">
                      + Add line
                    </button>
                  </h4>
                  {(estimate.laborItems || []).map((li, idx) => (
                    <div key={idx} className="grid grid-cols-6 gap-2 items-center">
                      <input
                        className="col-span-3 input-field"
                        value={li.description}
                        onChange={(e) => updateLabor(idx, 'description', e.target.value)}
                        placeholder="Description"
                      />
                      <input
                        className="col-span-1 input-field"
                        type="number"
                        value={li.hours}
                        onChange={(e) => updateLabor(idx, 'hours', parseFloat(e.target.value) || 0)}
                      />
                      <input
                        className="col-span-2 input-field"
                        type="number"
                        value={li.rate}
                        onChange={(e) => updateLabor(idx, 'rate', parseFloat(e.target.value) || 0)}
                      />
                    </div>
                  ))}
                  <div className="text-xs text-gray-500">Subtotal (labor): ₹{totals.labor.toLocaleString()}</div>
                </div>

                <div className="space-y-3">
                  <h4 className="text-sm font-bold text-gray-700 flex items-center justify-between">
                    Parts
                    <button type="button" onClick={addPart} className="text-xs font-bold text-blue-600 hover:text-blue-700">
                      + Add Part
                    </button>
                  </h4>
                  {(estimate.parts || []).map((p, idx) => (
                    <div key={idx} className="grid grid-cols-6 gap-2 items-center">
                      <input
                        className="col-span-3 input-field"
                        value={p.name}
                        onChange={(e) => updatePart(idx, 'name', e.target.value)}
                        placeholder="Part name"
                      />
                      <input
                        className="col-span-1 input-field"
                        type="number"
                        value={p.quantity}
                        onChange={(e) => updatePart(idx, 'quantity', parseInt(e.target.value, 10) || 0)}
                      />
                      <input
                        className="col-span-2 input-field"
                        type="number"
                        value={p.price ?? p.unitPrice ?? 0}
                        onChange={(e) => updatePart(idx, 'price', parseFloat(e.target.value) || 0)}
                      />
                    </div>
                  ))}
                  <div className="text-xs text-gray-500">Subtotal (parts): ₹{totals.parts.toLocaleString()}</div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Estimated completion date</label>
                  <input
                    type="datetime-local"
                    className="input-field"
                    value={estimate.estimatedCompletionDate}
                    onChange={(e) => setEstimate((p) => ({ ...p, estimatedCompletionDate: e.target.value }))}
                  />
                </div>
                <div className="md:col-span-2 flex items-end justify-end">
                  <button
                    type="button"
                    onClick={submitEstimate}
                    className="btn-primary w-full md:w-auto flex items-center justify-center gap-2"
                    disabled={action === 'estimate'}
                  >
                    {action === 'estimate' ? <FaSpinner className="animate-spin" /> : <FaCheckCircle />}
                    {action === 'estimate' ? 'Saving…' : job.status === 'PendingCustomerApproval' ? 'Save quotation' : 'Save job card updates'}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="space-y-4 text-sm text-gray-700">
              <p className="text-gray-500">Read-only snapshot of the agreed quotation for this stage.</p>
              <div>
                <h4 className="font-bold text-gray-800 mb-2">Labor</h4>
                {(job.estimate?.laborItems || []).length === 0 ? (
                  <p className="text-gray-500">None</p>
                ) : (
                  <ul className="space-y-2">
                    {(job.estimate?.laborItems || []).map((li, i) => (
                      <li key={i} className="flex flex-wrap justify-between gap-2 border-b border-gray-100 pb-2">
                        <span>{li.description || '—'}</span>
                        <span className="text-gray-600">
                          {li.hours}h × ₹{Number(li.rate || 0).toLocaleString()} = ₹
                          {(Number(li.hours || 0) * Number(li.rate || 0)).toLocaleString()}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <h4 className="font-bold text-gray-800 mb-2">Parts</h4>
                {(job.estimate?.parts || []).length === 0 ? (
                  <p className="text-gray-500">None</p>
                ) : (
                  <ul className="space-y-2">
                    {(job.estimate?.parts || []).map((p, i) => (
                      <li key={i} className="flex flex-wrap justify-between gap-2 border-b border-gray-100 pb-2">
                        <span>{p.name || p.sku || '—'}</span>
                        <span className="text-gray-600">
                          ×{p.quantity || 1} @ ₹{Number(p.price ?? p.unitPrice ?? 0).toLocaleString()}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Actions + Diagnosis & quotation */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex flex-wrap gap-2 justify-end">
          {(job.status === 'CustomerApproved' || job.status === 'PartsOrdered') && (
            <button
              type="button"
              onClick={() => goStatus('InProgress', 'Repair started')}
              className="btn-primary flex items-center gap-2 px-4 py-2"
              disabled={action === 'InProgress'}
              title="Start repair"
              aria-label="Start repair"
            >
              {action === 'InProgress' ? <FaSpinner size={18} className="animate-spin" /> : <FaTools size={18} />}
              <span className="text-xs sm:text-sm font-semibold whitespace-nowrap">Start repair</span>
            </button>
          )}
          {job.status === 'InProgress' && (
            <button
              type="button"
              onClick={() => goStatus('ReadyForQC', 'Work completed; ready for QC')}
              className="btn-primary flex items-center gap-2 px-4 py-2"
              disabled={action === 'ReadyForQC'}
              title="Mark ready for QC"
              aria-label="Mark ready for QC"
            >
              {action === 'ReadyForQC' ? <FaSpinner size={18} className="animate-spin" /> : <FaClipboardCheck size={18} />}
              <span className="text-xs sm:text-sm font-semibold whitespace-nowrap">Mark ready for QC</span>
            </button>
          )}
          {job.status === 'QCRejected' && (
            <>
              <button
                type="button"
                onClick={() => goStatus('InProgress', 'Returned to repair after QC rejection')}
                className="btn-outline flex items-center gap-2 px-4 py-2"
                disabled={action === 'InProgress'}
                title="Continue repair"
                aria-label="Continue repair"
              >
                {action === 'InProgress' ? <FaSpinner size={18} className="animate-spin" /> : <FaTools size={18} />}
                <span className="text-xs sm:text-sm font-semibold whitespace-nowrap">Continue repair</span>
              </button>
              <button
                type="button"
                onClick={() => goStatus('ReadyForQC', 'Resubmitted for QC after addressing feedback')}
                className="btn-primary flex items-center gap-2 px-4 py-2"
                disabled={action === 'ReadyForQC'}
                title="Resubmit for QC"
                aria-label="Resubmit for QC"
              >
                {action === 'ReadyForQC' ? <FaSpinner size={18} className="animate-spin" /> : <FaClipboardCheck size={18} />}
                <span className="text-xs sm:text-sm font-semibold whitespace-nowrap">Resubmit for QC</span>
              </button>
            </>
          )}
          {job.status === 'QCApproved' && (
            <button
              type="button"
              onClick={() => goStatus('Billed', 'Invoice generated')}
              className="btn-primary flex items-center gap-2 px-4 py-2"
              disabled={action === 'Billed'}
              title="Generate invoice"
              aria-label="Generate invoice"
            >
              {action === 'Billed' ? <FaSpinner size={18} className="animate-spin" /> : <FaFileInvoice size={18} />}
              <span className="text-xs sm:text-sm font-semibold whitespace-nowrap">Generate invoice</span>
            </button>
          )}
          {job.status === 'Paid' && (
            <>
              <button type="button" onClick={() => setDeliverModal(true)} className="btn-primary" disabled={action === 'deliver'}>
                {action === 'deliver' ? 'Delivering…' : 'Deliver Vehicle'}
              </button>
              {deliverModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                  <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Vehicle Delivery</h3>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Receiver Name *</label>
                        <input
                          type="text"
                          value={deliverForm.receiverName}
                          onChange={(e) => setDeliverForm((p) => ({ ...p, receiverName: e.target.value }))}
                          className="w-full border rounded-lg px-3 py-2"
                          placeholder="Name of person receiving vehicle"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Photo URL (optional)</label>
                        <input
                          type="text"
                          value={deliverForm.photo}
                          onChange={(e) => setDeliverForm((p) => ({ ...p, photo: e.target.value }))}
                          className="w-full border rounded-lg px-3 py-2"
                          placeholder="URL of handover photo"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Signature (optional)</label>
                        <input
                          type="text"
                          value={deliverForm.signature}
                          onChange={(e) => setDeliverForm((p) => ({ ...p, signature: e.target.value }))}
                          className="w-full border rounded-lg px-3 py-2"
                          placeholder="Signature reference or base64"
                        />
                      </div>
                    </div>
                    <div className="flex gap-2 mt-4">
                      <button type="button" onClick={deliver} disabled={!deliverForm.receiverName?.trim() || action === 'deliver'} className="flex-1 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50">
                        Confirm Delivery
                      </button>
                      <button type="button" onClick={() => setDeliverModal(false)} className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
          {job.status === 'Delivered' && (
            <button type="button" onClick={() => goStatus('Closed', 'Job closed')} className="btn-outline" disabled={action === 'Closed'}>
              {action === 'Closed' ? 'Closing…' : 'Close Job'}
            </button>
          )}
        </div>
      </div>

      {showEstimate && (job.status === 'Intake' || !estimateLockedToIntake) && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <h3 className="text-lg font-bold text-gray-900">Diagnosis &amp; quotation</h3>
              {estimateLockedToIntake && (
                <p className="text-sm text-amber-800 mt-1">Complete steps above and set status to <strong>INTAKE</strong> to send a quotation.</p>
              )}
            </div>
            {!estimateLockedToIntake && (
              <div className="text-sm font-semibold text-gray-700">
                Total: <span className="font-black text-indigo-700">₹{totals.total.toLocaleString()}</span>
              </div>
            )}
          </div>

          {!estimateLockedToIntake && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <h4 className="text-sm font-bold text-gray-700">Labor</h4>
                  {(estimate.laborItems || []).map((li, idx) => (
                    <div key={idx} className="grid grid-cols-6 gap-2 items-center">
                      <input className="col-span-3 input-field" value={li.description} onChange={(e) => updateLabor(idx, 'description', e.target.value)} placeholder="Description" />
                      <input className="col-span-1 input-field" type="number" value={li.hours} onChange={(e) => updateLabor(idx, 'hours', parseFloat(e.target.value) || 0)} />
                      <input className="col-span-2 input-field" type="number" value={li.rate} onChange={(e) => updateLabor(idx, 'rate', parseFloat(e.target.value) || 0)} />
                    </div>
                  ))}
                  <div className="text-xs text-gray-500">Subtotal (labor): ₹{totals.labor.toLocaleString()}</div>
                </div>

                <div className="space-y-3">
                  <h4 className="text-sm font-bold text-gray-700 flex items-center justify-between">
                    Parts
                    <button type="button" onClick={addPart} className="text-xs font-bold text-blue-600 hover:text-blue-700">
                      + Add Part
                    </button>
                  </h4>
                  {(estimate.parts || []).map((p, idx) => (
                    <div key={idx} className="grid grid-cols-6 gap-2 items-center">
                      <input className="col-span-3 input-field" value={p.name} onChange={(e) => updatePart(idx, 'name', e.target.value)} placeholder="Part name" />
                      <input className="col-span-1 input-field" type="number" value={p.quantity} onChange={(e) => updatePart(idx, 'quantity', parseInt(e.target.value, 10) || 0)} />
                      <input className="col-span-2 input-field" type="number" value={p.price} onChange={(e) => updatePart(idx, 'price', parseFloat(e.target.value) || 0)} />
                    </div>
                  ))}
                  <div className="text-xs text-gray-500">Subtotal (parts): ₹{totals.parts.toLocaleString()}</div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Estimated completion date</label>
                  <input
                    type="datetime-local"
                    className="input-field"
                    value={estimate.estimatedCompletionDate}
                    onChange={(e) => setEstimate((p) => ({ ...p, estimatedCompletionDate: e.target.value }))}
                  />
                </div>
                <div className="md:col-span-2 flex items-end justify-end">
                  <button type="button" onClick={submitEstimate} className="btn-primary w-full md:w-auto flex items-center justify-center gap-2" disabled={action === 'estimate'}>
                    {action === 'estimate' ? <FaSpinner className="animate-spin" /> : <FaCheckCircle />}
                    {action === 'estimate' ? 'Submitting…' : 'Send quotation for customer approval'}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Walk-in / legacy: Created + confirmed without forcing Intake — allow quote from Created if not customer allocation flow */}
      {showEstimate && job.status === 'Created' && isAccepted && (
        <p className="text-xs text-center text-gray-500">
          Tip: After you set <strong>INTAKE</strong>, the quotation form unlocks here. Garage-created jobs also follow the same steps.
        </p>
      )}

      {job.status !== 'Created' && job.status !== 'Intake' && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
          {job.status === 'PendingCustomerApproval' && (
            <div className="rounded-xl border-2 border-green-200 bg-green-50/80 p-4 space-y-3">
              <div>
                <h3 className="text-base font-bold text-green-900">Customer Bridge — parts approval</h3>
                <p className="text-sm text-green-800 mt-1">
                  Send this link to the customer on WhatsApp. They approve parts and pay without logging in.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={handleGenerateBridgeLink}
                  disabled={bridgeLoading}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-green-600 text-white font-semibold text-sm hover:bg-green-700 disabled:opacity-50"
                >
                  {bridgeLoading ? <FaSpinner className="animate-spin" /> : <FaLink />}
                  {bridgeLink ? 'Copy link again' : 'Generate & copy link'}
                </button>
                {displayBridgeLink && (
                  <>
                    <a
                      href={displayBridgeLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-white border border-green-300 text-green-900 font-semibold text-sm hover:bg-green-100"
                    >
                      Preview Bridge
                    </a>
                    <a
                      href={customerWhatsAppBridgeUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-white border border-green-300 text-green-900 font-semibold text-sm hover:bg-green-100"
                    >
                      <FaWhatsapp className="text-green-600" /> Send on WhatsApp
                    </a>
                  </>
                )}
              </div>
              {displayBridgeLink && (
                <p className="text-xs text-green-900/80 break-all font-mono bg-white/70 rounded-lg p-2 border border-green-100">
                  {displayBridgeLink}
                </p>
              )}
            </div>
          )}

          {['Billed', 'Paid', 'Delivered', 'Closed'].includes(job.status) && (
            <div className="rounded-xl border-2 border-indigo-200 bg-indigo-50/80 p-4 space-y-3">
              <div>
                <h3 className="text-base font-bold text-indigo-900">Customer Bridge — collection (CV-04/05)</h3>
                <p className="text-sm text-indigo-800 mt-1">
                  Vehicle ready? Send this link so the customer can pay labour, get a delivery OTP, and download the invoice.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={handleGenerateCollectionLink}
                  disabled={collectionLoading}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-indigo-600 text-white font-semibold text-sm hover:bg-indigo-700 disabled:opacity-50"
                >
                  {collectionLoading ? <FaSpinner className="animate-spin" /> : <FaLink />}
                  {collectionLink ? 'Copy link again' : 'Generate & copy link'}
                </button>
                {displayCollectionLink && (
                  <>
                    <a
                      href={displayCollectionLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-white border border-indigo-300 text-indigo-900 font-semibold text-sm hover:bg-indigo-100"
                    >
                      Preview collection
                    </a>
                    <a
                      href={customerWhatsAppCollectionUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-white border border-indigo-300 text-indigo-900 font-semibold text-sm hover:bg-indigo-100"
                    >
                      <FaWhatsapp className="text-green-600" /> Send on WhatsApp
                    </a>
                  </>
                )}
              </div>
              {displayCollectionLink && (
                <p className="text-xs text-indigo-900/80 break-all font-mono bg-white/70 rounded-lg p-2 border border-indigo-100">
                  {displayCollectionLink}
                </p>
              )}
            </div>
          )}

          {['InProgress', 'CustomerApproved', 'PartsOrdered'].includes(job.status) && (
            <div className="rounded-xl border-2 border-amber-200 bg-amber-50/80 p-4 space-y-3">
              <div>
                <h3 className="text-base font-bold text-amber-900">Customer Bridge — amendment (CV-AMD)</h3>
                <p className="text-sm text-amber-800 mt-1">
                  Mechanic found extra work? Send a WhatsApp link for the customer to approve or decline additional scope.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <label className="md:col-span-2 block">
                  <span className="text-xs font-semibold text-amber-900">New issue (plain language)</span>
                  <textarea
                    className="mt-1 w-full rounded-lg border border-amber-200 p-2 text-sm"
                    rows={2}
                    placeholder="e.g. Worn rear brake pads found during inspection"
                    value={amendmentForm.plainIssue}
                    onChange={(e) => setAmendmentForm((p) => ({ ...p, plainIssue: e.target.value }))}
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-semibold text-amber-900">Additional part</span>
                  <input
                    className="mt-1 w-full rounded-lg border border-amber-200 p-2"
                    placeholder="Rear brake pads"
                    value={amendmentForm.partName}
                    onChange={(e) => setAmendmentForm((p) => ({ ...p, partName: e.target.value }))}
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-semibold text-amber-900">Part price (₹)</span>
                  <input
                    type="number"
                    className="mt-1 w-full rounded-lg border border-amber-200 p-2"
                    placeholder="1200"
                    value={amendmentForm.partPrice}
                    onChange={(e) => setAmendmentForm((p) => ({ ...p, partPrice: e.target.value }))}
                  />
                </label>
                <label className="md:col-span-2 block">
                  <span className="text-xs font-semibold text-amber-900">Additional labour (₹)</span>
                  <input
                    type="number"
                    className="mt-1 w-full rounded-lg border border-amber-200 p-2"
                    placeholder="500"
                    value={amendmentForm.laborAmount}
                    onChange={(e) => setAmendmentForm((p) => ({ ...p, laborAmount: e.target.value }))}
                  />
                </label>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={handleGenerateAmendmentLink}
                  disabled={amendmentLoading}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-amber-600 text-white font-semibold text-sm hover:bg-amber-700 disabled:opacity-50"
                >
                  {amendmentLoading ? <FaSpinner className="animate-spin" /> : <FaLink />}
                  {amendmentLink ? 'Copy amendment link' : 'Generate & copy amendment link'}
                </button>
                {displayAmendmentLink && (
                  <a
                    href={displayAmendmentLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-white border border-amber-300 text-amber-900 font-semibold text-sm hover:bg-amber-100"
                  >
                    Preview amendment
                  </a>
                )}
              </div>
              {displayAmendmentLink && (
                <p className="text-xs text-amber-900/80 break-all font-mono bg-white/70 rounded-lg p-2 border border-amber-100">
                  {displayAmendmentLink}
                </p>
              )}
            </div>
          )}

          <h3 className="text-lg font-bold text-gray-900 mb-3">Estimate summary</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-xs text-gray-500 font-semibold">Subtotal</div>
              <div className="text-lg font-black">₹{estimateSummarySubtotal.toLocaleString()}</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-xs text-gray-500 font-semibold">Tax</div>
              <div className="text-lg font-black">₹{estimateSummaryTax.toLocaleString()}</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-xs text-gray-500 font-semibold">Total</div>
              <div className="text-lg font-black text-indigo-700">₹{estimateSummaryTotal.toLocaleString()}</div>
            </div>
          </div>
        </div>
      )}

      {loading && (
        <div className="fixed bottom-6 right-6 bg-black text-white text-xs px-3 py-2 rounded-lg flex items-center gap-2 shadow-lg">
          <FaSpinner className="animate-spin" /> Refreshing…
        </div>
      )}

      {rejectModal.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setRejectModal({ open: false, preset: '', reason: '' })}>
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-gray-900 mb-2">Reallocate service request</h3>
            <p className="text-sm text-gray-600 mb-3">
              This will send the job back for <span className="font-mono">reallocate</span>. The reason will be visible to the customer.
            </p>
            <div className="space-y-2 mb-3">
              {REJECT_PRESETS.map((p) => (
                <label key={p.value} className="flex items-start gap-2 cursor-pointer text-sm">
                  <input
                    type="radio"
                    name="rejectPreset"
                    checked={rejectModal.preset === p.value}
                    onChange={() => setRejectModal((prev) => ({ ...prev, preset: p.value, reason: p.text }))}
                    className="mt-1"
                  />
                  <span>{p.label}</span>
                </label>
              ))}
              <label className="flex items-start gap-2 cursor-pointer text-sm">
                <input
                  type="radio"
                  name="rejectPreset"
                  checked={rejectModal.preset === 'other'}
                  onChange={() => setRejectModal((prev) => ({ ...prev, preset: 'other' }))}
                  className="mt-1"
                />
                <span>Other (specify below)</span>
              </label>
            </div>
            <textarea
              rows={3}
              value={rejectModal.reason}
              onChange={(e) => setRejectModal((prev) => ({ ...prev, reason: e.target.value }))}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-500"
              placeholder="Reason sent with reallocation…"
            />
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={() => setRejectModal({ open: false, preset: '', reason: '' })} className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">
                Cancel
              </button>
              <button
                type="button"
                onClick={async () => {
                  const reason = (rejectModal.reason || '').trim();
                  if (!reason) {
                    alert('Please select or enter a reason.');
                    return;
                  }
                  try {
                    setAction('reject');
                    await jobCardService.rejectGarage(jobId, reason);
                    setRejectModal({ open: false, preset: '', reason: '' });
                    await refresh();
                  } catch (e) {
                    alert(e.message || 'Failed to reject job');
                  } finally {
                    setAction('');
                  }
                }}
                className="px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-semibold hover:bg-amber-700"
              >
                Confirm reallocate
              </button>
            </div>
          </div>
        </div>
      )}

      <InfoAlertCard
        open={bridgePartsAlert}
        onClose={() => setBridgePartsAlert(false)}
        variant="warning"
        title="Add spare parts first"
        message={
          'Bridge is for customer parts approval — labour-only estimates (like General Labor) cannot be approved here.\n\nOpen QUOTATION → add parts → Save, then generate the link again.'
        }
        actionLabel="Got it"
      />
    </div>
  );
};

export default JobCardWorkflow;
